import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Heart } from "lucide-react";
import { Brand } from "../components/NavBar";
import { getAnswers, RELATIONSHIP_LABELS, HELP_LABELS } from "../lib/onboarding";

const BEHAVIOR_LABELS = {
  memory: "making you question your memory",
  blame: "blaming you",
  accountability: "avoiding accountability",
  guilt: "guilt around boundaries",
  withdrawal: "affection followed by withdrawal",
  dismiss: "dismissing your feelings",
  control: "controlling behavior",
  off: "a sense that something feels off",
};

export default function Transition() {
  const navigate = useNavigate();
  const a = getAnswers();
  const firstName = (a.first_name || "").trim();
  const otherName = (a.other_name || "").trim();

  // If someone lands here without doing onboarding, send them to it.
  useEffect(() => {
    if (!firstName && !a.relationship) navigate("/onboarding", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const behaviors = (Array.isArray(a.behaviors) ? a.behaviors : []).map((k) => BEHAVIOR_LABELS[k]).filter(Boolean);
  const relationship = RELATIONSHIP_LABELS[a.relationship];
  const help = HELP_LABELS[a.help_with];

  const Pill = ({ children }) => (
    <span className="chip chip-neutral" style={{ fontSize: 13 }}>{children}</span>
  );

  return (
    <>
      <header className="nav">
        <div className="container-nc nav-inner">
          <Brand onClick={() => navigate("/")} />
        </div>
      </header>

      <main className="container-nc" style={{ paddingTop: 54, paddingBottom: 100, maxWidth: 580 }}>
        <div className="fade-up" data-testid="transition-screen">
          <span style={{ width: 54, height: 54, borderRadius: "50%", background: "var(--brand-tertiary)", display: "grid", placeItems: "center" }}>
            <Heart size={26} color="var(--brand)" />
          </span>
          <h1
            data-testid="transition-title"
            style={{ fontSize: "clamp(2rem, 5vw, 2.8rem)", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1.12, margin: "22px 0 10px" }}
          >
            Let's make sense of what you've been experiencing{firstName ? `, ${firstName}` : ""}.
          </h1>

          <p style={{ color: "var(--muted)", fontSize: 16, lineHeight: 1.6 }}>
            Here's what you told us — we'll keep it in mind as you analyze your conversations.
          </p>

          <div className="card card-pad" style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 18 }}>
            {otherName && (
              <Line label="Who" value={<Pill>{otherName}</Pill>} />
            )}
            {relationship && (
              <Line label="Relationship" value={<Pill>{relationship}</Pill>} />
            )}
            {behaviors.length > 0 && (
              <Line
                label="What concerns you"
                value={<span style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{behaviors.map((b) => <Pill key={b}>{b}</Pill>)}</span>}
              />
            )}
            {help && (
              <Line label="You want help with" value={<Pill>{help}</Pill>} />
            )}
          </div>

          <p style={{ fontSize: 18, fontWeight: 600, textAlign: "center", margin: "30px 0 4px", letterSpacing: "-0.01em" }} data-testid="transition-reassure">
            You deserve clarity, not more confusion.
          </p>
        </div>
      </main>

      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "var(--surface)", borderTop: "1px solid var(--border)", padding: "14px 0" }}>
        <div className="container-nc" style={{ maxWidth: 580 }}>
          <button className="btn btn-primary btn-lg btn-block" onClick={() => navigate("/paywall")} data-testid="transition-continue">
            Continue <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </>
  );
}

const Line = ({ label, value }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <span className="eyebrow">{label}</span>
    <div>{value}</div>
  </div>
);
